/**
 * Persönliche Einstellungen (Backend: /protected/users/me/settings).
 *
 * Der Zustand ist modulweit geteilt: Die Einstellung in "Mein Profil" und der
 * Schnellumschalter in der Korrespondenz zeigen und ändern denselben Wert.
 */
import { reactive } from 'vue'
import { API_BASE_URL } from '@/config'

export type CorrespondenceGrouping = 'participant' | 'group'

/** Leere Ordner in einer Ordneransicht: eingeblendet (Standard) oder ausgeblendet. */
export type EmptyFolderMode = 'show' | 'hide'

export interface UserSettings {
  correspondenceGrouping: CorrespondenceGrouping
  /** Dokumentenliste der Akte (Entscheidung 24.10.2026: Standard „show“). */
  caseDocsEmptyFolders: EmptyFolderMode
  /** Downloads – eigener Schalter, unabhängig von der Akte. */
  downloadsEmptyFolders: EmptyFolderMode
}

const DEFAULTS: UserSettings = {
  correspondenceGrouping: 'participant',
  caseDocsEmptyFolders: 'show',
  downloadsEmptyFolders: 'show',
}

const state = reactive<{ settings: UserSettings, loaded: boolean }>({ settings: { ...DEFAULTS }, loaded: false })
let pending: Promise<UserSettings> | null = null
/**
 * Während eines laufenden Ladens gespeicherte Werte. Sie sind neuer als das
 * Ladeergebnis und dürfen davon nicht überschrieben werden (Kundenfeedback
 * Kanzlei Polz 27.10.2026, I.2: Klick auf „Leere ausblenden“ direkt nach dem
 * Öffnen der Akte wirkte sonst nicht).
 */
let savedDuringLoad: Partial<UserSettings> = {}

const URL = `${API_BASE_URL}/protected/users/me/settings`

const emptyFolderMode = (value: unknown): EmptyFolderMode => (value === 'hide' ? 'hide' : 'show')

/** Nur bekannte Werte übernehmen; alles andere fällt auf den Standard zurück. */
export function sanitizeUserSettings (data: Partial<Record<keyof UserSettings, unknown>> | null): UserSettings {
  return {
    correspondenceGrouping: data?.correspondenceGrouping === 'group' ? 'group' : DEFAULTS.correspondenceGrouping,
    caseDocsEmptyFolders: emptyFolderMode(data?.caseDocsEmptyFolders),
    downloadsEmptyFolders: emptyFolderMode(data?.downloadsEmptyFolders),
  }
}

/** Lädt die Einstellungen einmal je Sitzung (weitere Aufrufe teilen den Abruf). */
export function loadUserSettings (force = false): Promise<UserSettings> {
  if (state.loaded && !force) return Promise.resolve(state.settings)
  if (pending && !force) return pending
  savedDuringLoad = {}
  const request: Promise<UserSettings> = fetch(URL, { credentials: 'include' })
    .then(res => (res.ok ? res.json() : null))
    .catch(() => null)
    .then((data) => {
      state.settings = { ...sanitizeUserSettings(data), ...savedDuringLoad }
      state.loaded = true
      if (pending === request) {
        pending = null
        savedDuringLoad = {}
      }
      return state.settings
    })
  pending = request
  return request
}

/** Speichert eine Einstellung; bei Fehler bleibt der alte Wert und es wird geworfen. */
export async function saveUserSetting<K extends keyof UserSettings> (key: K, value: UserSettings[K]): Promise<void> {
  const previous = state.settings[key]
  state.settings = { ...state.settings, [key]: value }
  if (pending) {
    savedDuringLoad = { ...savedDuringLoad, [key]: value }
  }
  const res = await fetch(URL, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ [key]: value }),
  }).catch(() => null)
  if (!res || !res.ok) {
    state.settings = { ...state.settings, [key]: previous }
    const { [key]: _failed, ...rest } = savedDuringLoad
    savedDuringLoad = rest
    throw new Error('Einstellung konnte nicht gespeichert werden')
  }
}

export function useUserSettings () {
  return state
}
